(() => {
  if (window.BJ4HomeDiscovery) {
    window.BJ4HomeDiscovery.initAll();
    return;
  }

  const touchQuery = window.matchMedia('(max-width: 749px), (hover: none)');

  const isTouchLayout = () => touchQuery.matches;

  const initLifestyle = (root) => {
    const panelGroup = root.querySelector('[data-bj4-lifestyle-panels]');
    const panels = Array.from(root.querySelectorAll('[data-bj4-lifestyle-panel]'));
    const triggers = panels.map((panel) => panel.querySelector('[data-bj4-lifestyle-trigger]'));
    const details = panels.map((panel) => panel.querySelector('.bj4-four-doorways__details'));

    if (!panelGroup || !panels.length || triggers.some((trigger) => !trigger) || details.some((detail) => !detail)) return false;

    const setActive = (nextIndex, allowCollapse = false) => {
      const currentIndex = panels.findIndex((panel) => panel.classList.contains('is-active'));
      const shouldCollapse = allowCollapse && isTouchLayout() && currentIndex === nextIndex;

      panels.forEach((panel, index) => {
        const active = !shouldCollapse && index === nextIndex;
        panel.classList.toggle('is-active', active);
        triggers[index].setAttribute('aria-expanded', String(active));
        details[index].hidden = !active;
      });
    };

    const moveFocus = (currentIndex, direction) => {
      let nextIndex = currentIndex + direction;
      if (nextIndex < 0) nextIndex = triggers.length - 1;
      if (nextIndex >= triggers.length) nextIndex = 0;
      triggers[nextIndex].focus();
      setActive(nextIndex);
    };

    panels.forEach((panel, index) => {
      const trigger = triggers[index];

      panel.addEventListener('pointerenter', () => {
        if (!isTouchLayout()) setActive(index);
      });

      panel.addEventListener('focusin', () => {
        if (!isTouchLayout()) setActive(index);
      });

      panel.addEventListener('click', (event) => {
        if (!isTouchLayout()) return;
        if (event.target.closest('a, button')) return;
        setActive(index, true);
      });

      trigger.addEventListener('click', () => setActive(index, true));
      trigger.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
          event.preventDefault();
          moveFocus(index, 1);
        } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
          event.preventDefault();
          moveFocus(index, -1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          triggers[0].focus();
          setActive(0);
        } else if (event.key === 'End') {
          event.preventDefault();
          triggers[triggers.length - 1].focus();
          setActive(triggers.length - 1);
        } else if (event.key === 'Escape' && isTouchLayout()) {
          event.preventDefault();
          panels.forEach((item, itemIndex) => {
            item.classList.remove('is-active');
            triggers[itemIndex].setAttribute('aria-expanded', 'false');
            details[itemIndex].hidden = true;
          });
        }
      });
    });

    const syncLayout = () => {
      if (isTouchLayout()) {
        panels.forEach((panel, index) => {
          panel.classList.remove('is-active');
          triggers[index].setAttribute('aria-expanded', 'false');
          details[index].hidden = true;
        });
      } else {
        const activeIndex = panels.findIndex((panel) => panel.classList.contains('is-active'));
        setActive(activeIndex < 0 ? 0 : activeIndex);
      }
    };

    touchQuery.addEventListener?.('change', syncLayout);
    syncLayout();
    panelGroup.classList.add('is-enhanced');
    return true;
  };

  const initCrafts = (root) => {
    const grid = root.querySelector('[data-bj4-craft-grid]');
    const triggers = Array.from(root.querySelectorAll('[data-bj4-craft-trigger]'));
    const panels = Array.from(root.querySelectorAll('[data-bj4-craft-panel]'));
    let pointerTimer = null;

    if (!grid || !triggers.length || triggers.length !== panels.length) return false;

    const clearPointerTimer = () => {
      if (pointerTimer === null) return;
      window.clearTimeout(pointerTimer);
      pointerTimer = null;
    };

    const setActive = (nextIndex, allowCollapse = false) => {
      const currentIndex = triggers.findIndex((trigger) => trigger.classList.contains('is-active'));
      const shouldCollapse = allowCollapse && isTouchLayout() && currentIndex === nextIndex;

      triggers.forEach((trigger, index) => {
        const active = !shouldCollapse && index === nextIndex;
        trigger.classList.toggle('is-active', active);
        trigger.setAttribute('aria-expanded', String(active));
        panels[index].classList.toggle('is-active', active);
        panels[index].hidden = !active;
      });
    };

    const collapseAll = () => {
      triggers.forEach((trigger, index) => {
        trigger.classList.remove('is-active');
        trigger.setAttribute('aria-expanded', 'false');
        panels[index].classList.remove('is-active');
        panels[index].hidden = true;
      });
    };

    const focusAndSelect = (nextIndex) => {
      const boundedIndex = Math.max(0, Math.min(triggers.length - 1, nextIndex));
      clearPointerTimer();
      triggers[boundedIndex].focus();
      setActive(boundedIndex);
    };

    triggers.forEach((trigger, index) => {
      trigger.addEventListener('pointerenter', () => {
        if (isTouchLayout() || trigger.classList.contains('is-active')) return;
        clearPointerTimer();
        pointerTimer = window.setTimeout(() => {
          pointerTimer = null;
          if (!isTouchLayout()) setActive(index);
        }, 100);
      });

      trigger.addEventListener('pointerleave', clearPointerTimer);

      trigger.addEventListener('click', (event) => {
        clearPointerTimer();
        if (!isTouchLayout()) return;

        const isLink = trigger.matches('a[href]');
        const isKeyboardActivation = event.detail === 0;
        const isActive = trigger.classList.contains('is-active');

        // On touch, the first tap reveals the explanatory panel and its
        // explicit CTA. A second tap follows the craft link. Keyboard
        // activation always follows the native link on the first press.
        if (isLink && (isKeyboardActivation || isActive)) return;

        event.preventDefault();
        if (isLink) setActive(index);
        else setActive(index, true);
      });
      trigger.addEventListener('focus', () => {
        if (!isTouchLayout()) {
          clearPointerTimer();
          setActive(index);
        }
      });
      trigger.addEventListener('keydown', (event) => {
        let nextIndex = null;

        if (event.key === 'ArrowRight') nextIndex = index + 1;
        if (event.key === 'ArrowLeft') nextIndex = index - 1;
        if (event.key === 'ArrowDown') nextIndex = index + 2;
        if (event.key === 'ArrowUp') nextIndex = index - 2;
        if (event.key === 'Home') nextIndex = 0;
        if (event.key === 'End') nextIndex = triggers.length - 1;

        if (nextIndex !== null) {
          event.preventDefault();
          focusAndSelect(nextIndex);
        } else if (event.key === 'Escape' && isTouchLayout()) {
          event.preventDefault();
          collapseAll();
        }
      });
    });

    const syncLayout = () => {
      clearPointerTimer();
      if (isTouchLayout()) {
        collapseAll();
      } else {
        const activeIndex = triggers.findIndex((trigger) => trigger.classList.contains('is-active'));
        setActive(activeIndex < 0 ? 0 : activeIndex);
      }
    };

    touchQuery.addEventListener?.('change', syncLayout);
    syncLayout();
    grid.classList.add('is-enhanced');
    return true;
  };

  const initRoot = (root) => {
    if (!root || root.dataset.bj4DiscoveryReady === 'true') return;
    let initialized = false;

    if (root.dataset.bj4DiscoveryRoot === 'lifestyle') initialized = initLifestyle(root);
    if (root.dataset.bj4DiscoveryRoot === 'crafts') initialized = initCrafts(root);
    if (initialized) root.dataset.bj4DiscoveryReady = 'true';
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-bj4-discovery-root]').forEach(initRoot);
  };

  window.BJ4HomeDiscovery = { initAll };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
